import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { LocalAuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';
import { TenantService } from '../services/tenant.service';
import { catchError, switchMap } from 'rxjs/operators';
import { SessionRevokedService, isSessionRevokedError } from '../services/session-revoked.service';

function resolvePaths(rawUrl: string): { requestPath: string; apiBasePath: string; requestOrigin: string; apiOrigin: string } {
  const origin = window.location.origin;
  const requestUrl = new URL(rawUrl, origin);
  const apiUrl = new URL(environment.apiurl || '/api', origin);

  const requestPath = normalizePath(requestUrl.pathname);
  const apiBasePath = normalizePath(apiUrl.pathname);

  return { requestPath, apiBasePath, requestOrigin: requestUrl.origin, apiOrigin: apiUrl.origin };
}

function normalizePath(path: string): string {
  if (!path) {
    return '/';
  }

  const normalized = path.startsWith('/') ? path : `/${path}`;
  return normalized.endsWith('/') && normalized.length > 1
    ? normalized.slice(0, -1)
    : normalized;
}

function isRefreshTokenRelatedError(error: unknown): boolean {
  const authError = error as {
    error?: string;
    error_description?: string;
    message?: string;
  };

  const code = String(authError?.error || '').toLowerCase();
  const description = String(authError?.error_description || '').toLowerCase();
  const message = String(authError?.message || '').toLowerCase();
  const fullText = `${code} ${description} ${message}`;

  return (
    fullText.includes('missing_refresh_token') ||
    fullText.includes('invalid refresh token') ||
    fullText.includes('invalid_grant') ||
    fullText.includes('login_required') ||
    fullText.includes('consent_required') ||
    // auth0-spa-js TimeoutError from the silent-auth iframe. When the app's origin is missing
    // from the Auth0 application's Allowed Web Origins, Auth0 rejects the prompt=none request
    // ("does not have a registered origin") and renders an error page instead of posting a
    // web_message back — so the SDK has nothing to receive and can only time out. That is a
    // dead end for the refresh-token fallback; interactive login is the sole way out.
    fullText.includes('timeout')
  );
}

/**
 * No access token means this request can never be sent, so the caller gets nothing back either
 * way. Say so explicitly before deciding how to recover: TenantService would otherwise stay in
 * its initial empty state and the side menu would render an auth failure as "Du bist noch keinem
 * Unternehmen zugeordnet" — sending the user to support for a problem that is not theirs.
 */
function handleTokenFailure(
  error: unknown,
  authService: LocalAuthService,
  tenantService: TenantService,
): Observable<never> {
  tenantService.markLoadFailed();

  if (isRefreshTokenRelatedError(error)) {
    // EMPTY rather than a rethrow: a full-page redirect to Auth0 is already under way, and every
    // in-flight request failing loudly on the way out would only produce error toasts the user
    // never gets to read.
    authService.loginWithReturnUrl();
    return EMPTY;
  }

  return throwError(() => error);
}

/**
 * 401 `session_revoked`: the token is still valid, but the user ended this session elsewhere. This is
 * checked on the API response only and never leads to a login redirect: the Auth0 session is still alive
 * and would sign the user straight back in. The error is re-thrown so callers still leave their loading state.
 */
function handleApiError(error: unknown, sessionRevoked: SessionRevokedService): Observable<never> {
  if (isSessionRevokedError(error)) {
    void sessionRevoked.handle();
  }

  return throwError(() => error);
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(LocalAuthService);
  const tenantService = inject(TenantService);
  const sessionRevoked = inject(SessionRevokedService);

  if (req.headers.has('x-skip-auth-interceptor')) {
    const passthroughRequest = req.clone({
      headers: req.headers.delete('x-skip-auth-interceptor'),
    });
    return next(passthroughRequest);
  }

  const { requestPath, apiBasePath, requestOrigin, apiOrigin } = resolvePaths(req.url);
  const isApiRequest =
    requestOrigin === apiOrigin &&
    (requestPath === apiBasePath || requestPath.startsWith(`${apiBasePath}/`));

  if (!isApiRequest) {
    return next(req);
  }

  return authService.getAccessTokenSilently().pipe(
    // Scoped to token acquisition on purpose. This used to sit at the end of the pipe, where it
    // also saw errors from the actual HTTP call — so an API response whose text happened to
    // contain one of the strings below could force a re-login, and the harmless-looking
    // 'timeout' rule added here would have turned every gateway timeout into one.
    catchError((error) => handleTokenFailure(error, authService, tenantService)),
    switchMap(token => {
      const isTenantLookup = requestPath === `${apiBasePath}/tenants/me`;
      const isUserPreferenceLookup = requestPath === `${apiBasePath}/users/me/preferences`;

      const baseHeaders: Record<string, string> = {
        Authorization: `Bearer ${token}`
      };

      if (isTenantLookup || isUserPreferenceLookup) {
        const authReq = req.clone({ setHeaders: baseHeaders });
        return next(authReq).pipe(catchError((error) => handleApiError(error, sessionRevoked)));
      }

      const tenantId =
        tenantService.getActiveTenantId() ??
        tenantService.getMainTenantId();

      const authReq = req.clone({
        setHeaders: tenantId
          ? {
            ...baseHeaders,
            'x-tenant-id': tenantId,
          }
          : baseHeaders,
      });

      return next(authReq).pipe(catchError((error) => handleApiError(error, sessionRevoked)));
    }),
  );
};
